<script>
  import { progress } from "../lib/state.svelte.js";
  import { CURRICULUM } from "../../data/problems/index.js";
  import { TRACKS, categoriesOf } from "../../data/taxonomy.js";
  import { diffMeta, firstSolveEvo, attemptStats, pickRandomProblem, trackProgress } from "../lib/economy.js";
  import { fmtEvo, fmtSolveTime, shortDate } from "../lib/format.js";
  import { nav, openProblem, go } from "../lib/nav.svelte.js";
  import { playSound } from "../lib/audio.js";
  import { canUseReview, markedProblems, unmark } from "../lib/review.svelte.js";
  import {
    canUseMissed,
    missedProblems,
    clearedProblems,
    forget,
    forgetCleared,
    startDrill,
  } from "../lib/missed.svelte.js";
  import { showToast } from "../lib/effects.js";

  const track = $derived(nav.trackId);
  const groups = $derived(
    categoriesOf(track)
      .map((c) => ({
        cat: c,
        items: CURRICULUM.filter((p) => p.category === c.id),
      }))
      .filter((g) => g.items.length > 0)
  );

  function selectTrack(id) {
    nav.trackId = id;
    playSound("click");
  }

  function surprise() {
    const id = pickRandomProblem(track) || pickRandomProblem();
    if (id) openProblem(id, "learn");
  }

  const review = $derived(canUseReview() ? markedProblems() : []);
  let reviewOpen = $state(false); // 初期は畳む（missOpen と同じ理由）

  function reviewAll() {
    if (review.length) openProblem(review[0].id, "learn");
  }

  /* 間違えた問題（自動記録・アカウント登録者限定） */
  const missed = $derived(canUseMissed() ? missedProblems() : []);
  const cleared = $derived(canUseMissed() ? clearedProblems() : []);
  const missedHere = $derived(missed.filter((p) => p.track === track));
  // 一覧は初期状態では畳んでおく。件数と「解き直す」だけ見えていれば足り、
  // 開いた瞬間にカテゴリ一覧が下へ押しやられるのを避ける。
  let missOpen = $state(false);
  let clearedOpen = $state(false);

  function drillAll(trackOnly) {
    const id = startDrill(trackOnly ? track : null);
    if (id) openProblem(id, "learn");
  }

  function clearHistory() {
    const n = forgetCleared();
    if (n) showToast(`克服済み ${n}問の記録を消しました`, 2.5);
  }

  function statusOf(p) {
    if (progress.solved[p.id]) return "solved";
    if ((progress.attempts[p.id] || []).length > 0) return "tried";
    return "";
  }
</script>

<section>
  <div class="track-tabs">
    {#each TRACKS as t (t.id)}
      {@const pr = trackProgress(t.id)}
      <button class:active={track === t.id} onclick={() => selectTrack(t.id)}>
        {t.em}
        {t.name}
        <span class="tiny">({pr.solved}/{pr.total})</span>
      </button>
    {/each}
  </div>

  <div class="card">
    <div class="section-head">
      <h2 style="margin:0">📚 問題を解いて、時代を進む</h2>
      <span class="spacer"></span>
      <button class="btn run" onclick={surprise}>🎲 おまかせ出題</button>
    </div>
    <p class="muted tiny" style="margin:0">
      難しい問題ほど大きく前進します（基礎 ×0.6 ／ 標準 ×1 ／ 応用 ×1.8）。一度解いた問題の復習でも
      30% ぶん前に進みます。
    </p>
  </div>

  <!-- 間違えた問題（自動記録・アカウント登録者限定） -->
  {#if !canUseMissed()}
    <div class="card miss-card locked">
      <div class="review-head">
        <span class="review-em">🎯</span>
        <div>
          <h3 style="margin:0">間違えた問題を解き直す</h3>
          <p class="muted tiny" style="margin:2px 0 0">
            一度間違えた問題が自動で記録され、あとからまとめて解き直せます。正解すると一覧から外れます。<br />
            <b>アカウント登録者限定</b>の機能です（記録は端末をまたいで同期されます）。
          </p>
        </div>
        <span class="spacer"></span>
        <button class="btn run" onclick={() => go("account")}>アカウントを作る</button>
      </div>
    </div>
  {:else if missed.length > 0 || cleared.length > 0}
    <div class="card miss-card">
      <div class="review-head">
        <span class="review-em">🎯</span>
        <div>
          <h3 style="margin:0">
            間違えた問題
            {#if missed.length > 0}<span class="count bad">{missed.length}問</span>{/if}
          </h3>
          <p class="muted tiny" style="margin:2px 0 0">
            {#if missed.length > 0}
              よく間違えた順に並んでいます。正解すると自動で外れます。
              {#if missedHere.length > 0 && missedHere.length < missed.length}
                （この分野に {missedHere.length}問）
              {/if}
            {:else}
              未克服の問題はありません。克服した {cleared.length}問はいつでも復習できます。
            {/if}
          </p>
        </div>
        <span class="spacer"></span>
        {#if missed.length > 0}
          <button class="btn submit" onclick={() => drillAll(false)}>
            ▶ 全部解き直す（{missed.length}）
          </button>
          {#if missedHere.length > 0 && missedHere.length < missed.length}
            <button class="btn run" onclick={() => drillAll(true)}>この分野だけ（{missedHere.length}）</button>
          {/if}
          <button class="btn ghost" onclick={() => (missOpen = !missOpen)}>
            {missOpen ? "▴ 閉じる" : "▾ 一覧を見る"}
          </button>
        {/if}
      </div>

      {#if missOpen && missed.length > 0}
        <div class="plist" style="margin-top:12px">
          {#each missed as p (p.id)}
            {@const m = diffMeta(p.diff)}
            <div class="pitem tried review-row">
              <button class="review-open" onclick={() => openProblem(p.id, "learn")}>
                <span class="chk bad">✗</span>
                <span class="ti">
                  <div>{p.title}</div>
                  <div class="tagrow">
                    {#each p.tags as tag}
                      <span class="tag">{tag}</span>
                    {/each}
                  </div>
                  <div class="hist">
                    {p.miss.n}回間違えた · 最終 {shortDate(new Date(p.miss.w).toISOString().slice(0, 10))}
                    {#if p.miss.c > 0}· 一度克服したが再び間違えた{/if}
                  </div>
                </span>
                <span
                  class="diff"
                  style="background:{m.color}22; color:{m.color}; border:1px solid {m.color}66"
                >
                  {m.label}
                </span>
              </button>
              <button class="review-del" onclick={() => forget(p.id)} title="記録から消す">✕</button>
            </div>
          {/each}
        </div>
      {/if}

      {#if cleared.length > 0}
        <div class="cleared-block">
          <button class="cleared-toggle" onclick={() => (clearedOpen = !clearedOpen)}>
            {clearedOpen ? "▾" : "▸"} 克服した問題 {cleared.length}問
          </button>
          {#if clearedOpen}
            <div class="plist" style="margin-top:10px">
              {#each cleared as p (p.id)}
                <div class="pitem solved review-row">
                  <button class="review-open" onclick={() => openProblem(p.id, "learn")}>
                    <span class="chk">🎯</span>
                    <span class="ti">
                      <div>{p.title}</div>
                      <div class="hist">
                        {p.miss.n}回間違えたのち克服 ·
                        {shortDate(new Date(p.miss.c).toISOString().slice(0, 10))}
                      </div>
                    </span>
                  </button>
                  <button class="review-del" onclick={() => forget(p.id)} title="記録から消す">✕</button>
                </div>
              {/each}
            </div>
            <button class="btn ghost tiny-btn" onclick={clearHistory}>克服済みの記録をまとめて消す</button>
          {/if}
        </div>
      {/if}
    </div>
  {/if}

  <!-- あとで解き直す（アカウント登録者限定） -->
  {#if !canUseReview()}
    <div class="card review-card locked">
      <div class="review-head">
        <span class="review-em">🔖</span>
        <div>
          <h3 style="margin:0">あとで解き直す</h3>
          <p class="muted tiny" style="margin:2px 0 0">
            気になった問題に印を付けて、あとからまとめて解き直せます。<br />
            <b>アカウント登録者限定</b>の機能です（端末をまたいで印が同期されます）。
          </p>
        </div>
        <span class="spacer"></span>
        <button class="btn run" onclick={() => go("account")}>アカウントを作る</button>
      </div>
    </div>
  {:else if review.length > 0}
    <div class="card review-card">
      <div class="review-head">
        <span class="review-em">🔖</span>
        <div>
          <h3 style="margin:0">あとで解き直す <span class="count">{review.length}問</span></h3>
          <p class="muted tiny" style="margin:2px 0 0">印を付けた問題です。解き直しでも 30% ぶん前に進みます。</p>
        </div>
        <span class="spacer"></span>
        <button class="btn run" onclick={reviewAll}>▶ 解き直す</button>
        <button class="btn ghost" onclick={() => (reviewOpen = !reviewOpen)}>
          {reviewOpen ? "▴ 閉じる" : "▾ 一覧を見る"}
        </button>
      </div>

      {#if reviewOpen}
        <div class="plist" style="margin-top:12px">
          {#each review as p (p.id)}
            {@const m = diffMeta(p.diff)}
            <div class="pitem {statusOf(p)} review-row">
              <button class="review-open" onclick={() => openProblem(p.id, "learn")}>
                <span class="chk">{progress.solved[p.id] ? "✓" : "🔁"}</span>
                <span class="ti">
                  <div>{p.title}</div>
                  <div class="tagrow">
                    {#each p.tags as tag}
                      <span class="tag">{tag}</span>
                    {/each}
                  </div>
                </span>
                <span
                  class="diff"
                  style="background:{m.color}22; color:{m.color}; border:1px solid {m.color}66"
                >
                  {m.label}
                </span>
              </button>
              <button class="review-del" onclick={() => unmark(p.id)} title="印を外す">✕</button>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  {/if}

  {#if groups.length === 0}
    <div class="card">
      <div class="empty-state">
        <span class="em">🧪</span>
        この分野の問題はまだ準備中です。
      </div>
    </div>
  {/if}

  {#each groups as g (g.cat.id)}
    {@const solved = g.items.filter((p) => progress.solved[p.id]).length}
    <div class="card category-section">
      <div class="category-title">
        <span>{g.cat.em}</span>
        <span>{g.cat.name}</span>
        <span class="spacer"></span>
        <span class="count">{solved} / {g.items.length}</span>
      </div>
      <div class="bar thin" style="margin-bottom:12px">
        <i style="width:{g.items.length ? (solved / g.items.length) * 100 : 0}%"></i>
      </div>

      <div class="plist">
        {#each g.items as p (p.id)}
          {@const m = diffMeta(p.diff)}
          {@const st = attemptStats(p.id)}
          <button class="pitem {statusOf(p)}" onclick={() => openProblem(p.id, "learn")}>
            <span class="chk">
              {progress.solved[p.id] ? "✓" : st.tries > 0 ? "🔁" : ""}
            </span>
            <span class="ti">
              <div>{p.title}</div>
              <div class="tagrow">
                {#each p.tags as tag}
                  <span class="tag">{tag}</span>
                {/each}
              </div>
              {#if st.tries > 0}
                <div class="hist">
                  {st.tries}回挑戦 · {st.ok}回正解
                  {#if st.best != null}· 最速 {fmtSolveTime(st.best)}{/if}
                  {#if st.last}· 最終 {shortDate(new Date(st.last.t).toISOString().slice(0, 10))}{/if}
                </div>
              {/if}
            </span>
            <span
              class="diff"
              style="background:{m.color}22; color:{m.color}; border:1px solid {m.color}66"
            >
              {m.label}
            </span>
            <span class="evo-tag">+{fmtEvo(firstSolveEvo(p.diff))}</span>
          </button>
        {/each}
      </div>
    </div>
  {/each}
</section>
